import { AdmRuleHistory, CreateFloorRule, FloorRule, UpdateFloorRule } from '@pubstack/common/src/adm/rules'
import { sortByName } from '@pubstack/common/src/sort'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from '../api-client'

export const FLOOR_RULES_QUERY_KEYS = ['adm', 'floor-rules']
const url = '/adm/floor-rules'

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/** `archived: true` asks for the set-aside rules instead of the live ones. Owner-only at the API. */
export const useFloorRules = (withStacksUse = false, archived = false) => {
  const urlWithSearchParams = buildUrlWithSearchParams(url, { ...(withStacksUse ? { withStacksUse } : {}), ...(archived ? { archived } : {}) })
  const query = useQuery<FloorRule[]>({ url: urlWithSearchParams, keys: [...FLOOR_RULES_QUERY_KEYS, { withStacksUse, archived }], select: sortByName })

  return { ...query, data: query.data || [] }
}

export const useFloorRule = (id: string, enabled: boolean = !!id) => {
  return useQuery<FloorRule>({
    url: `${url}/${id}`,
    keys: [...FLOOR_RULES_QUERY_KEYS, id],
    enabled,
  })
}

export const useFloorRulesWithHistory = () => {
  return useQuery<AdmRuleHistory>({
    url: `${url}/history`,
    keys: [...FLOOR_RULES_QUERY_KEYS, 'history'],
  })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateFloorRule = (toasts: MutationToasts = {}) => {
  return useMutation<CreateFloorRule, FloorRule>({
    method: 'post',
    url,
    collectionKeys: FLOOR_RULES_QUERY_KEYS,
    ...toasts,
  })
}

export const useUpdateFloorRule = (toasts: MutationToasts = {}) => {
  return useMutation<UpdateFloorRule, FloorRule>({
    method: 'put',
    url,
    collectionKeys: FLOOR_RULES_QUERY_KEYS,
    ...toasts,
  })
}

/**
 * Set a rule aside, or bring it back. Owner-only at the API, and refused while any stack still uses it.
 *
 * `collectionKeys` covers the live list and the archived one alike — they share a prefix — so a rule
 * leaving one and appearing in the other refetches both.
 */
export const useArchiveFloorRule = (toasts: MutationToasts = {}) => {
  return useMutation<{ id: string; archived: boolean }, FloorRule>({
    method: 'put',
    url: ({ id }) => `${url}/${id}/archive`,
    collectionKeys: FLOOR_RULES_QUERY_KEYS,
    ...toasts,
  })
}
